import { useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import { supabase } from "@/integrations/supabase/client";
import { Tables } from "@/integrations/supabase/types";
import { useSocialMediaMeta } from "@/hooks/useSocialMediaMeta";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ExternalLink, Heart } from "lucide-react";
import { Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";

type ConfirmedSponsor = Tables<"confirmed_sponsors">;
type SponsorshipTier = Tables<"sponsorship_tiers">;

interface SponsorWithTier extends ConfirmedSponsor {
  sponsorship_tier?: SponsorshipTier;
}

export default function MeetOurSponsors() {
  const [sponsors, setSponsors] = useState<SponsorWithTier[]>([]);
  const [loading, setLoading] = useState(true);
  useSocialMediaMeta({
    title: "Meet Our Corporate Sponsors - Bobo & Teedee®",
    description: "Discover the amazing companies supporting educational entertainment for children in the Cayman Islands."
  });

  useEffect(() => {
    const fetchSponsors = async () => {
      try {
        const { data, error } = await supabase
          .from("confirmed_sponsors")
          .select(`
            *,
            sponsorship_tier:sponsorship_tiers(*)
          `)
          .eq("is_active", true)
          .order("display_order", { ascending: true });

        if (error) throw error;
        setSponsors(data || []);
      } catch (error) {
        console.error("Error fetching sponsors:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchSponsors();
  }, []);

  // Group sponsors by tier level
  const sponsorsByTier = sponsors.reduce((acc, sponsor) => {
    const tierLevel = sponsor.sponsorship_tier?.level || 999;
    if (!acc[tierLevel]) acc[tierLevel] = [];
    acc[tierLevel].push(sponsor);
    return acc;
  }, {} as Record<number, SponsorWithTier[]>);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-background via-background/95 to-primary/5">
        <div className="container mx-auto px-4 py-16">
          <div className="text-center">
            <div className="animate-pulse space-y-4">
              <div className="h-12 bg-muted rounded w-96 mx-auto"></div>
              <div className="h-6 bg-muted rounded w-64 mx-auto"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <Navbar />
      <Helmet>
        <title>Meet Our Corporate Sponsors - Bobo & Teedee®</title>
        <meta name="description" content="Discover the amazing companies supporting educational entertainment for children in the Cayman Islands." />
        <meta property="og:title" content="Meet Our Corporate Sponsors - Bobo & Teedee®" />
        <meta property="og:description" content="Discover the amazing companies supporting educational entertainment for children in the Cayman Islands." />
        <meta property="og:url" content={`${window.location.origin}/meet-our-sponsors`} />
        <meta property="og:image" content={`${window.location.origin}/img/social/og-default.png`} />
        <meta name="twitter:title" content="Meet Our Corporate Sponsors - Bobo & Teedee®" />
        <meta name="twitter:description" content="Discover the amazing companies supporting educational entertainment for children in the Cayman Islands." />
        <meta name="twitter:image" content={`${window.location.origin}/img/social/twitter-default.png`} />
      </Helmet>

      <div className="min-h-screen bg-gradient-to-br from-background via-background/95 to-primary/5">
        <div className="container mx-auto px-4 py-16">
          {/* Header */}
          <div className="text-center mb-16">
            <h1 className="text-4xl md:text-6xl font-bold mb-6 bg-gradient-to-r from-primary via-secondary to-accent bg-clip-text text-transparent">
              Meet Our Corporate Sponsors
            </h1>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto mb-8">
              We're grateful to these amazing companies who believe in educational entertainment 
              and support our mission to create engaging content for children in the Cayman Islands.
            </p>
            <Link to="/support">
              <Button size="lg" className="gap-2">
                <Heart className="h-5 w-5" />
                Become a Sponsor
              </Button>
            </Link>
          </div>

          {/* Sponsors by Tier */}
          {Object.keys(sponsorsByTier).length === 0 ? (
            <div className="text-center py-16">
              <Heart className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-2xl font-semibold mb-4">Looking for Our First Sponsors!</h3>
              <p className="text-muted-foreground mb-8 max-w-2xl mx-auto">
                We're actively seeking corporate partners to support our educational mission. 
                Your company could be the first to appear on this page!
              </p>
              <Link to="/support">
                <Button size="lg">Explore Sponsorship Opportunities</Button>
              </Link>
            </div>
          ) : (
            <div className="space-y-16">
              {Object.entries(sponsorsByTier)
                .sort(([a], [b]) => Number(a) - Number(b))
                .map(([tierLevel, tierSponsors]) => {
                  const tier = tierSponsors[0]?.sponsorship_tier;
                  return (
                    <div key={tierLevel} className="space-y-8">
                      {tier && (
                        <div className="text-center">
                          <Badge variant="outline" className="text-lg px-4 py-2 mb-4">
                            {tier.name} Sponsors
                          </Badge>
                        </div>
                      )}
                      
                      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                        {tierSponsors.map((sponsor) => (
                          <Card 
                            key={sponsor.id} 
                            className="group hover:shadow-xl transition-all duration-300 border-2 hover:border-primary/20"
                          >
                            <CardHeader className="text-center">
                              {sponsor.logo_url && sponsor.recognition_level !== 'name_only' && (
                                <div className="mb-4">
                                  <img
                                    src={sponsor.logo_url}
                                    alt={`${sponsor.company_name} logo`}
                                    className="h-20 w-auto mx-auto object-contain"
                                  />
                                </div>
                              )}
                              <CardTitle className="text-xl group-hover:text-primary transition-colors">
                                {sponsor.company_name}
                              </CardTitle>
                            </CardHeader>
                            
                            {(sponsor.description || sponsor.thank_you_message) && sponsor.recognition_level === 'full' && (
                              <CardContent className="text-center space-y-4">
                                {sponsor.description && (
                                  <p className="text-muted-foreground">
                                    {sponsor.description}
                                  </p>
                                )}
                                {sponsor.thank_you_message && (
                                  <blockquote className="italic text-primary/80 border-l-4 border-primary/20 pl-4">
                                    "{sponsor.thank_you_message}"
                                  </blockquote>
                                )}
                                {sponsor.website_url && (
                                  <Button variant="outline" size="sm" asChild>
                                    <a 
                                      href={sponsor.website_url} 
                                      target="_blank" 
                                      rel="noopener noreferrer"
                                      className="gap-2"
                                    >
                                      Visit Website
                                      <ExternalLink className="h-4 w-4" />
                                    </a>
                                  </Button>
                                )}
                              </CardContent>
                            )}
                          </Card>
                        ))}
                      </div>
                    </div>
                  );
                })}
            </div>
          )}

          {/* Call to Action */}
          <div className="text-center mt-16 pt-16 border-t">
            <h3 className="text-2xl font-semibold mb-4">Join Our Community of Supporters</h3>
            <p className="text-muted-foreground mb-8 max-w-2xl mx-auto">
              Ready to make a difference in children's education? Explore our sponsorship opportunities 
              and become part of our mission.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/support">
                <Button size="lg">Corporate Sponsorship</Button>
              </Link>
              <Link to="/meet-our-donors">
                <Button variant="outline" size="lg">Meet Our Individual Donors</Button>
              </Link>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </>
  );
}